import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography, IconButton, Divider } from "@mui/material";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import Wheel from "@uiw/react-color-wheel";
import ShadeSlider from "@uiw/react-color-shade-slider";
import Grid from "@mui/material/Grid2";
import { hexToHsva, hsvaToHex } from "../../utils/utils";
import ThemePreview from "../../components/themes/ThemePreview";
import useWindowSize from "../../hooks/useWindowSize";
import { get, patch } from "../../services/apiService";
import { toast } from "react-toastify";
import CircularLoader from "../../components/common/ScreenLoader";
import BackdropLoader from "../../components/common//BackdropLoader";
import i18next from "i18next";


const ThemePage = () => {
  const { width } = useWindowSize();
  const [hsva, setHsva] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [themeId, setThemeId] = useState(null);

  const cmyk = ["#01A0C6", "#F0047F", "#FFFF00", "#000000"];
  const rgb = ["#FF0000", "#00FF00", "#0000FF"];
  const greyScale = ["#E3E3E3", "#6C6D6F", "#2E2E2E"];
  const hsb = ["#78D6F2", "#04ACF5", "#0D6892"];
  const [isLoading, setIsLoading] = useState(true);
  const [backdropLoading, setBackdropLoading] = useState(false);

  const userId = localStorage.getItem("userId");

  const handleColor = (hexaColor, index) => {
    setActiveIndex(index);
    const hsvaColor = hexToHsva(hexaColor);
    setHsva((prev) => ({ ...prev, ...hsvaColor }));
  };

  const onLoading = (isLoading) => {
    setIsLoading(isLoading);
  };

  const onBackDropLoading = (isLoading) => {
    setBackdropLoading(isLoading);
  };

  const fetchUserTheme = useCallback(async () => {
    const response = await get(`users/${userId}/theme`, {}, onLoading);
    if (response?.status === 200) {
      const hexaColor = response.data.primaryColor || "#E3E3E3";
      const hsvaColor = hexToHsva(hexaColor);
      setHsva((prev) => ({ ...prev, ...hsvaColor }));
      setThemeId(response.data.id || null);
    }
  }, [userId]);

  useEffect(() => {
    fetchUserTheme();
  }, [fetchUserTheme]);

  const handleSaveTheme = async (themeObject) => {
    const response = await patch(
      `users/configure/theme/${userId}`,
      themeObject,
      {},
      onBackDropLoading
    );
    if (response?.status === 200) {
      toast.success(i18next.t("Theme has been updated successfully"));
    }
  };

  return (
    <Box sx={{ p: width > 450 ? 3 : 0.8 }}>
      {backdropLoading && <BackdropLoader />}
      <Box
        sx={{
          borderRadius: "8px",
          padding: "20px",
          marginBottom: "2%",
          backgroundColor: "white",
          marginRight: "1%",
          marginLeft: "1%",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Tajawal-Bold",
            color: "#0A0A0A",
            fontSize: width > 900 ? "26px" : "18px",
            fontWeight: 700,
          }}
        >
          Select Theme
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: "white",
          padding: "26px",
          height: hsva ? "1200px" : "auto",
          borderRadius: "14px",
          marginRight: "1%",
          marginLeft: "1%",
        }}
      >
        <Typography
          sx={{
            fontSize: width > 900 ? "24px" : "16px",
            fontWeight: 700,
            color: "#2E2E2E",
            mb: 1,
          }}
        >
          Themes
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#C91515", fontSize: "12px", fontWeight: 500, mb: 3 }}
        >
          If a theme is picked by a user, you cannot edit or delete the theme*
        </Typography>
        <Divider sx={{ borderBottomWidth: 2, borderColor: "#EFF4FA" }} />

        {isLoading ? (
          <Grid
            item
            size={{ xs: 12 }}
            sx={{ textAlign: "center", height: "100%", width: "100%" }}
          >
            <Box
              sx={{
                height: "500px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularLoader />
            </Box>
          </Grid>
        ) : hsva ? (
          <>
            <Grid
              container
              spacing={3}
              sx={{ paddingLeft: "7%", paddingRight: "7%", marginTop: "3%" }}
            >
              {/* Left Panel */}
              <Grid
                item
                size={{ lg: 4, md: 4, sm: 4, xs: 6 }}
                sx={{ display: { xs: "none", sm: "block" } }}
              >
                {[
                  { title: "CMYK", colors: cmyk },
                  { title: "RGB", colors: rgb },
                  { title: "Grey Scale", colors: greyScale },
                  { title: "HSB", colors: hsb },
                ].map((group) => (
                  <Box key={group.title} sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex" }}>
                      {group.colors.map((color, index) => (
                        <Box
                          key={`${group.title}-${index}`}
                          sx={{
                            height: "13px",
                            width: "45px",
                            backgroundColor: color,
                            mt: 1,
                            mr: 1,
                            borderRadius: "6px",
                          }}
                        />
                      ))}
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontFamily: "Tajawal-Bold",
                        color: "#9C9FA2",
                        fontSize: "26px",
                      }}
                    >
                      {group.title}
                    </Typography>
                  </Box>
                ))}
              </Grid>

              {/* Center Panel */}
              <Grid item size={{ lg: 4, md: 6, sm: 6, xs: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    cursor: "pointer",
                  }}
                >
                  <Wheel
                    color={hsva}
                    onChange={(color) =>
                      setHsva((prev) => ({ ...prev, ...color.hsva }))
                    }
                  />
                  <ShadeSlider
                    hsva={hsva}
                    style={{ width: 210, marginTop: 20 }}
                    onChange={(newShade) =>
                      setHsva((prev) => ({ ...prev, ...newShade }))
                    }
                  />
                </Box>
              </Grid>

              {/* Right Panel */}
              <Grid item size={{ lg: 4, md: 2, sm: 2, xs: 6 }}>
                <Box
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "flex-end",
                    // backgroundColor:"orange"
                  }}
                >
                  <Box sx={{ flexDirection: "column", alignItems: "center" }}>
                    {[
                      "#FF2600",
                      "#7524AF",
                      "#0229BA",
                      "#00B600",
                      "#FFFB00",
                    ].map((color, index) => (
                      <Box
                        key={`theme-color-${index}`}
                        sx={{
                          backgroundColor: color,
                          height: "50px",
                          width: "50px",
                          borderRadius: "50%",
                          mt: 2,
                          position: "relative",
                        }}
                        onClick={() => handleColor(color, index)}
                      >
                        {activeIndex === index && (
                          <IconButton
                            sx={{
                              position: "absolute",
                              right: "-12px",
                              top: "-12px",
                              backgroundColor: "#fff",
                            }}
                          >
                            <ColorLensIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            </Grid>
            <ThemePreview
              themeColor={hsvaToHex(hsva?.h, hsva?.s, hsva?.v, hsva?.a)}
              onSaveTheme={handleSaveTheme}
              themeId={themeId}
            />
          </>
        ) : (
          <Grid item xs={12} sx={{ height: "70%", width: "100%" }}>
            <Box
              sx={{
                // height: "100%",
                height: "500px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                // textAlign: "center",
                // background: "grey",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    textAlign: "center",
                    fontFamily: "Tajawal-Bold",
                    fontWeight: 600,
                    fontSize: "24px",
                  }}
                >
                  Theme Palette is not available.
                </Typography>
                <Typography
                  sx={{
                    marginTop: "3%",
                    textAlign: "center",
                    fontFamily: "Tajawal-Bold",
                    fontWeight: 700,
                    fontSize: "22px",
                    color: "#00000099",
                  }}
                >
                  In the Theme Palette, you can change the theme color and
                  preview it.
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default ThemePage;
